import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { User, Mail, IdCard, Phone } from "lucide-react";
import { jsPDF } from "jspdf";
import emailjs from "@emailjs/browser";
import { REGISTER_URL, STAFF_URL } from "../../apiConfig";
import { Lock, Loader2, CheckCircle2, Check, ClipboardList, ArrowDown, AlertTriangle, Zap } from "lucide-react";
import SearchableDropdown from "../SearchableDropdown";

// Initialize EmailJS
emailjs.init("ns8iBr8YNGxt5OpPN");

// Helper function to load logo as base64
const loadLogoAsBase64 = async () => {
  try {
    const response = await fetch('/logo.png');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading logo:', error);
    return null;
  }
};

export default function TraineeForm({ courseData, lookups }) {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // 🟢 CUSTOM ALERT STATE & OVERRIDE
  const [customAlert, setCustomAlert] = useState({ isOpen: false, message: "" });
  const alert = (message) => {
    setCustomAlert({ isOpen: true, message: String(message) });
  };

  // 🟢 GOOGLE RECAPTCHA v3 STATE
  const [recaptchaToken, setRecaptchaToken] = useState("");

  // 🟢 RECAPTCHA v3 SITE KEY - Replace with your actual key
  const RECAPTCHA_SITE_KEY = "6LdhwBcsAAAAAKDrnrh8YCtsmNmn7B2ZOvA7GroR";

  const [formData, setFormData] = useState({
    studentName: "",
    idType: "",
    nric: "",
    cardType: "",
    dob: "",
    certificate: null,
    contact: "",
    email: "",
    gender: "",
    nationality: "",
    race: "",
    courseName: courseData?.title || "",
    courseAmount: courseData?.price ? (courseData.price * 1.09).toFixed(2) : "",
    promoCode: "",
    knowHow: "",
    knowStaff: "No",
    workPermit: null,
    wsqCert: null,
    educationCert: null,
    eCert: "",
    staffName: "",
    staffEmail: "",
    terms: false,
    whatsapp: false,
  });

  // 🟢 AUTO-FETCH COURSE DATA EFFECT
  useEffect(() => {
    if (courseData?.price) {
      const gstAmount = (courseData.price * 0.09).toFixed(2);
      const totalWithGst = (parseFloat(courseData.price) + parseFloat(gstAmount)).toFixed(2);

      setFormData(prev => ({
        ...prev,
        courseName: courseData.title || "",
        courseAmount: totalWithGst
      }));
    }
  }, [courseData]);

  // 🟢 RECAPTCHA v3 INITIALIZATION
  useEffect(() => {
    const loadRecaptcha = () => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
      script.addEventListener('load', () => {
        console.log('reCAPTCHA v3 loaded');
      });
      document.body.appendChild(script);
    };

    loadRecaptcha();
  }, []);

  // 🟢 EXECUTE RECAPTCHA v3
  const executeRecaptcha = async () => {
    try {
      if (!window.grecaptcha) {
        console.error('reCAPTCHA not loaded');
        return null;
      }

      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
        action: 'trainee_registration'
      });
      setRecaptchaToken(token);
      return token;
    } catch (error) {
      console.error('reCAPTCHA execution failed:', error);
      return null;
    }
  };

  // 🟢 SALES STAFF AUTO-LOOKUP STATE
  const [staffLookupState, setStaffLookupState] = useState('idle'); // 'idle' | 'loading' | 'found' | 'not_found' | 'error'
  const [staffOfficialName, setStaffOfficialName] = useState('');
  const staffDebounceTimer = useRef(null);

  // 🟢 SALES STAFF AUTO-LOOKUP EFFECT — debounced, triggers after staffName has 3+ chars
  useEffect(() => {
    const name = formData.staffName.trim();
    if (name.length < 3) {
      setStaffLookupState('idle');
      setStaffOfficialName('');
      return;
    }

    if (staffDebounceTimer.current) clearTimeout(staffDebounceTimer.current);
    setStaffLookupState('loading');

    staffDebounceTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${STAFF_URL}?name=${encodeURIComponent(name)}`);
        const data = await res.json();

        if (data.success && data.found && data.staff) {
          const s = data.staff;
          setStaffLookupState('found');
          setStaffOfficialName(s.staff_name || '');
          // Auto-fill staff fields
          setFormData(prev => ({
            ...prev,
            staffName: s.staff_name || prev.staffName,
            staffEmail: s.email || prev.staffEmail,
          }));
        } else {
          setStaffLookupState('not_found');
          setStaffOfficialName('');
        }
      } catch (err) {
        console.error('Staff lookup error:', err);
        setStaffLookupState('error');
        setStaffOfficialName('');
      }
    }, 700);

    return () => clearTimeout(staffDebounceTimer.current);
  }, [formData.staffName]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (name === "knowStaff" && value === "No") {
      setStaffLookupState('idle');
      setStaffOfficialName('');
      setFormData(prev => ({
        ...prev,
        knowStaff: "No",
        staffName: "",
        staffEmail: "",
      }));
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : type === "file" ? files[0] : value,
      });
    }
  };

  // 🔥 GENERATE PDF FUNCTION - WITH ACTUAL LOGO IMAGE
  const generatePDF = async () => {
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleDateString('en-GB');
    let yPos = 15;

    // Logo and Header - Enhanced Design with Visual Logo
    // Green background header (taller for logo)
    doc.setFillColor(11, 153, 73);
    doc.rect(0, 0, 210, 40, 'F');

    // Try to add actual logo image
    try {
      const logoBase64 = await loadLogoAsBase64();
      if (logoBase64) {
        // Add logo image (20x20mm at position 15,10)
        doc.addImage(logoBase64, 'PNG', 15, 10, 20, 20);
      }
    } catch (error) {
      console.error('Could not add logo to PDF:', error);
    }

    // Company name next to logo
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(26);
    doc.setFont("helvetica", "bold");
    doc.text("GREENMARK", 45, 18);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text("Training Institute", 45, 25);

    // Form title
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("TRAINEE REGISTRATION FORM", 105, 35, { align: "center" });

    yPos = 50;
    doc.setTextColor(0, 0, 0);

    // Personal Information
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("PERSONAL INFORMATION:", 14, yPos);
    yPos += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Student Name: ${formData.studentName}`, 14, yPos);
    yPos += 6;
    doc.text(`ID Type: ${formData.idType}`, 14, yPos);
    yPos += 6;
    doc.text(`NRIC/FIN: ${formData.nric}`, 14, yPos);
    yPos += 6;
    doc.text(`Date of Birth: ${formData.dob || 'N/A'}`, 14, yPos);
    yPos += 6;
    doc.text(`Gender: ${formData.gender || 'N/A'}`, 14, yPos);
    yPos += 6;
    doc.text(`Nationality: ${formData.nationality || 'N/A'}`, 14, yPos);
    yPos += 6;
    doc.text(`Race: ${formData.race || 'N/A'}`, 14, yPos);
    yPos += 6;
    doc.text(`Contact: ${formData.contact}`, 14, yPos);
    yPos += 6;
    doc.text(`Email: ${formData.email}`, 14, yPos);
    yPos += 11;

    // Course Information
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("COURSE INFORMATION:", 14, yPos);
    yPos += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Course Name: ${formData.courseName}`, 14, yPos);
    yPos += 6;
    doc.text(`Level: ${courseData?.selectedLevel?.toUpperCase() || 'N/A'}`, 14, yPos);
    yPos += 6;
    doc.text(`Total Amount (incl. GST): $${formData.courseAmount}`, 14, yPos);
    yPos += 11;

    // Additional Information
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("ADDITIONAL INFORMATION:", 14, yPos);
    yPos += 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`How did you know about us: ${formData.knowHow || 'N/A'}`, 14, yPos);
    yPos += 6;
    doc.text(`Staff Name: ${formData.staffName || 'N/A'}`, 14, yPos);
    yPos += 6;
    doc.text(`Staff Email: ${formData.staffEmail || 'N/A'}`, 14, yPos);
    yPos += 15;

    // Footer with Contact Information
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFillColor(245, 245, 245);
    doc.rect(0, yPos, 210, 30, 'F');
    doc.setFontSize(10);
    doc.setTextColor(11, 153, 73);
    doc.setFont("helvetica", "bold");
    doc.text("GREENMARK Training Institute", 105, yPos + 6, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(8);
    doc.text("Email: pcm@greenmark.com.sg | Phone: +65 8014 7799", 105, yPos + 12, { align: "center" });
    doc.text("Website: www.greenmark.com.sg", 105, yPos + 17, { align: "center" });
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(`Generated on: ${currentDate}`, 105, yPos + 24, { align: "center" });

    return doc;
  };

  // 🔥 SEND EMAIL WITH COMPLETE HTML BODY - ENHANCED WITH LOGO AND CONTACT INFO
  const sendEmailWithPDF = async () => {
    try {
      // Complete email HTML with all details - ENHANCED VERSION
      const fullEmailHTML = `
        <table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4; padding:20px; font-family:Arial, sans-serif;">
          <tr>
            <td>
              <table width="100%" cellpadding="0" cellspacing="0" 
                style="max-width:650px; margin:auto; background:#ffffff; border-radius:8px; overflow:hidden; box-shadow:0 2px 8px rgba(0,0,0,0.1);">

                <!-- LOGO HEADER -->
                <tr>
                  <td style="background:#0b9949; padding:30px 20px; text-align:center;">
                    <h1 style="color:#ffffff; margin:0; font-size:32px; font-family:Arial, sans-serif; letter-spacing:1px;">GREENMARK</h1>
                    <p style="color:#ffffff; margin:8px 0 0 0; font-size:14px; font-family:Arial, sans-serif;">Training Institute</p>
                  </td>
                </tr>

        
                <!-- PERSONAL INFO CARD -->
                <tr>
                  <td style="padding:20px; font-family:Arial; font-size:14px; color:#333;">
                    <h3 style="color:#0b9949; margin:0 0 15px 0; border-bottom:2px solid #0b9949; padding-bottom:8px;">👤 Personal Information</h3>

                    <table width="100%" cellpadding="8" cellspacing="0" style="background:#f9f9f9; border-radius:6px;">
                      <tr><td style="padding:8px;"><strong>Student Name:</strong></td><td style="padding:8px;">${formData.studentName}</td></tr>
                      <tr><td style="padding:8px; background:#fff;"><strong>ID Type:</strong></td><td style="padding:8px; background:#fff;">${formData.idType}</td></tr>
                      <tr><td style="padding:8px;"><strong>NRIC/FIN:</strong></td><td style="padding:8px;">${formData.nric}</td></tr>
                      <tr><td style="padding:8px; background:#fff;"><strong>Date of Birth:</strong></td><td style="padding:8px; background:#fff;">${formData.dob || "N/A"}</td></tr>
                      <tr><td style="padding:8px;"><strong>Gender:</strong></td><td style="padding:8px;">${formData.gender || "N/A"}</td></tr>
                      <tr><td style="padding:8px; background:#fff;"><strong>Nationality:</strong></td><td style="padding:8px; background:#fff;">${formData.nationality || "N/A"}</td></tr>
                      <tr><td style="padding:8px;"><strong>Race:</strong></td><td style="padding:8px;">${formData.race || "N/A"}</td></tr>
                      <tr><td style="padding:8px; background:#fff;"><strong>Contact:</strong></td><td style="padding:8px; background:#fff;">${formData.contact}</td></tr>
                      <tr><td style="padding:8px;"><strong>Email:</strong></td><td style="padding:8px;">${formData.email}</td></tr>
                    </table>
                  </td>
                </tr>

                <!-- COURSE INFO CARD -->
                <tr>
                  <td style="padding:20px; font-family:Arial; font-size:14px; color:#333;">
                    <h3 style="color:#0b9949; margin:0 0 15px 0; border-bottom:2px solid #0b9949; padding-bottom:8px;">📚 Course Information</h3>

                    <table width="100%" cellpadding="8" cellspacing="0" style="background:#f9f9f9; border-radius:6px;">
                      <tr><td style="padding:8px;"><strong>Course Name:</strong></td><td style="padding:8px;">${formData.courseName}</td></tr>
                      <tr><td style="padding:8px; background:#fff;"><strong>Course Level:</strong></td><td style="padding:8px; background:#fff;">${courseData?.selectedLevel?.toUpperCase() || 'N/A'}</td></tr>
                      <tr><td style="padding:8px;"><strong>Total Amount (incl. GST):</strong></td><td style="padding:8px;"><span style="color:#0b9949; font-size:20px; font-weight:bold;">$${formData.courseAmount}</span></td></tr>
                    </table>
                  </td>
                </tr>

                <!-- ADDITIONAL INFO -->
                <tr>
                  <td style="padding:20px; font-family:Arial; font-size:14px;">
                    <h3 style="color:#0b9949; margin:0 0 15px 0; border-bottom:2px solid #0b9949; padding-bottom:8px;">📌 Additional Information</h3>

                    <p style="margin:8px 0;"><strong>How did you know us:</strong> ${formData.knowHow || "N/A"}</p>
                    <p style="margin:8px 0;"><strong>Staff Name:</strong> ${formData.staffName || "N/A"}</p>
                    <p style="margin:8px 0;"><strong>Staff Email:</strong> ${formData.staffEmail || "N/A"}</p>
                  </td>
                </tr>

                <!-- FOOTER WITH CONTACT INFO -->
                <tr>
                  <td style="background:#f9f9f9; padding:25px 20px; text-align:center; font-size:13px; color:#666; font-family:Arial; border-top:3px solid #0b9949;">
                    <p style="margin:0 0 10px 0; color:#0b9949; font-weight:bold; font-size:16px;">GREENMARK Training Institute</p>
                    <p style="margin:5px 0; color:#555;">📧 Email: <a href="mailto:pcm@greenmark.com.sg" style="color:#0b9949; text-decoration:none;">info@greenmark.com.sg</a></p>
                    <p style="margin:5px 0; color:#555;">📞 Phone: <a href="tel:+658014 7799" style="color:#0b9949; text-decoration:none;">+65 1234 5678</a></p>
                    <p style="margin:5px 0; color:#555;">🌐 Website: <a href="http://www.greenmark.com.sg" style="color:#0b9949; text-decoration:none;">www.greenmark.com.sg</a></p>
                    <p style="margin:15px 0 5px 0; font-size:11px; color:#999;">Generated on: <strong>${new Date().toLocaleDateString("en-GB")}</strong></p>
                    <p style="margin:5px 0; font-size:11px; color:#999;">Thank you for choosing Greenmark. We will contact you soon with further details.</p>
                  </td>
                </tr>

              </table>
            </td>
          </tr>
        </table>
      `;

      console.log("📧 Sending complete email with all details...");

      // Send email using EmailJS with complete HTML body
      const response = await emailjs.send(
        "service_7pytgxf",
        "template_lbjy8n3",
        {
          message_html: fullEmailHTML,
          to_email: "pcm@greenmark.com.sg",
          reply_to: formData.email || "noreply@greenmark.com.sg"
        }
      );

      console.log("✅ Email sent successfully:", response);
      return true;
    } catch (error) {
      alert("⚠️ Please accept the Terms and Conditions before submitting.");
      return;
    }
  };

  // 🔥 SUCCESS MODAL COMPONENT (Individual Learner "Skill-Up Celebration" screen)
  const SuccessModal = ({ onClose }) => {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-gray-900/75 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, y: 20, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-100 overflow-hidden text-center flex flex-col p-8"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check size={36} className="text-green-600 stroke-[3]" />
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-2">Registration Submitted!</h3>
          <p className="text-gray-600 mb-8">
            Thank you for registering, <strong>{formData.studentName}</strong>. 
            We have received your details and will review and respond within a few working days.
          </p>
          <button
            onClick={onClose}
            className="w-full bg-[#0b9949] hover:bg-[#087c3a] text-white py-3 rounded-xl font-bold text-sm tracking-wide shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Go to Home
          </button>
        </motion.div>
      </motion.div>
    );
  };

  // 🔥 HANDLE FORM SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.studentName || !formData.idType || !formData.nric || !formData.email) {
      alert("❌ Please fill all required fields!");
      return;
    }

    setIsSubmitting(true);

    try {
      // 🟢 Execute reCAPTCHA v3
      const token = await executeRecaptcha();
      if (!token) {
        console.warn("reCAPTCHA token not generated, continuing anyway...");
      }

      console.log("🔄 Generating PDF...");
      // Generate PDF (now async)
      const pdfDoc = await generatePDF();

      // Convert files to base64 helper
      const toBase64 = (file) => {
        if (!file) return Promise.resolve(null);
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      };

      const nricBase64 = await toBase64(formData.workPermit);
      const wsqBase64 = await toBase64(formData.wsqCert);
      const eduBase64 = await toBase64(formData.educationCert);

      // 🟢 Register in database via local PHP proxy
      const registerPayload = {
        sponsorType: "self",
        schedule_id: courseData?.schedule?.schedule_id || null,
        course_id: courseData?.schedule?.course_id || null,
        location_id: courseData?.schedule?.location_id || 1,
        language: courseData?.schedule?.course_language || "English",
        course_type: "Public Class",
        discount: 0,
        promo_code: formData.promoCode || "",
        staffName: formData.staffName || "",
        staffEmail: formData.staffEmail || "",
        trainees: [
          {
            studentName: formData.studentName,
            nric: formData.nric,
            idType: formData.idType || "NRIC",
            cardType: formData.cardType || "NRIC",
            dob: formData.dob || null,
            contact: formData.contact,
            email: formData.email,
            gender: formData.gender || "",
            nationality: formData.nationality || "",
            race: formData.race || "",
            nricUpload: nricBase64,
            wsqCertUpload: wsqBase64,
            educationCert: eduBase64
          }
        ]
      };

      console.log("💾 Registering trainee in database via PHP proxy...", registerPayload);
      const regResponse = await fetch(REGISTER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(registerPayload)
      });

      if (!regResponse.ok) {
        throw new Error(`Failed to save registration to database: ${regResponse.statusText}`);
      }

      const regResult = await regResponse.json();
      console.log("✅ Database registration response:", regResult);
      if (!regResult.success) {
        throw new Error(regResult.error || "Failed to persist trainee registration in database.");
      }

      console.log("📧 Sending email with PDF...");
      // Send email with PDF
      await sendEmailWithPDF();

      // Show success modal
      setShowSuccessModal(true);

    } catch (error) {
      console.error("❌ Submission error:", error);
      alert(`❌ Error: ${error.message || 'Failed to send email. Please try again or contact support.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 🔥 HANDLE MODAL CLOSE AND REDIRECT
  const handleModalClose = () => {
    window.location.href = "/";
  };

  // 🟢 COURSE SUMMARY COMPONENT
  const renderCourseSummary = () => {
    if (!courseData?.title) return null;

    const basePrice = courseData.price || 0;
    const gstAmount = (basePrice * 0.09).toFixed(2);
    const totalWithGst = (parseFloat(basePrice) + parseFloat(gstAmount)).toFixed(2);

    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-green-800 mb-3 flex items-center gap-2"><ClipboardList size={20} /> Course Summary (Auto-filled)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <div><span className="font-medium">Course:</span> {courseData.title}</div>
          <div><span className="font-medium">Level:</span> {courseData.selectedLevel?.toUpperCase() || 'N/A'}</div>
          <div><span className="font-medium">Duration:</span> {courseData.duration || 'N/A'}</div>
          <div><span className="font-medium">Base Price:</span> ${basePrice}</div>
          <div><span className="font-medium">GST (9%):</span> ${gstAmount}</div>
          <div><span className="font-medium text-green-700 font-bold">Total Amount:</span> ${totalWithGst}</div>
        </div>
      </div>
    );
  };

  if (submitted) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex flex-col items-center justify-center h-screen bg-gradient-to-br from-[#0b9949] to-[#097c3c] text-white"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 120, damping: 10 }}
            className="bg-white text-[#0b9949] p-8 rounded-full shadow-2xl"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-16 h-16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.5 12.75l6 6 9-13.5"
              />
            </svg>
          </motion.div>

          <motion.h2
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-3xl font-bold mt-6"
          >
            Thank You for Registering!
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="text-lg mt-3 max-w-md text-center text-greensafe-50"
          >
            We've received your details and will contact you shortly.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            className="mt-8 text-sm text-greensafe-100"
          >
            Redirecting to home page...
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <>
      <AnimatePresence>
        {customAlert.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-gray-100 overflow-hidden text-center flex flex-col p-6"
            >
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${customAlert.message.includes("Error") || customAlert.message.includes("Failed") || customAlert.message.includes("⚠️") || customAlert.message.includes("❌") ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'}`}>
                {customAlert.message.includes("Successfully") ? <CheckCircle2 size={32} /> : <AlertTriangle size={32} />}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Notification</h3>
              <p className="text-gray-600 mb-6 text-sm whitespace-pre-wrap">{customAlert.message}</p>
              <button
                onClick={() => setCustomAlert({ ...customAlert, isOpen: false })}
                className="w-full bg-gray-900 hover:bg-black text-white py-2.5 rounded-xl font-bold text-sm shadow-md transition-all active:scale-95"
              >
                Acknowledge
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-5xl mx-auto bg-white p-8 shadow-lg rounded-md mt-10 mb-20">
        <div className="space-y-8">

        {/* 🟢 AUTO-FILLED COURSE SUMMARY */}
        {renderCourseSummary()}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* TRAINEE DETAILS SECTION */}
          <section className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
            <h2 className="text-2xl font-bold text-slate-800 mb-6 pb-2 border-b-2 border-slate-100 flex items-center gap-3">
              <span className="bg-[#0b9949] text-white w-8 h-8 rounded-lg flex items-center justify-center text-sm">1</span>
              Trainee Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              {/* Student Name */}
              <div className="flex flex-col relative group">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 transition-colors group-focus-within:text-[#0b9949]">Student Name *</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"><User className="w-4 h-4" /></span>
                  <input
                    name="studentName"
                    placeholder="Enter full name"
                    className="border-2 border-slate-200 rounded-xl pl-10 pr-4 py-3 w-full text-sm font-medium bg-white focus:border-[#0b9949] focus:ring-4 focus:ring-green-50 focus:outline-none transition-all duration-300"
                    onChange={handleChange}
                    value={formData.studentName}
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="flex flex-col relative group">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 transition-colors group-focus-within:text-[#0b9949]">Email Address *</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"><Mail className="w-4 h-4" /></span>
                  <input
                    name="email"
                    type="email"
                    placeholder="Enter email address"
                    className="border-2 border-slate-200 rounded-xl pl-10 pr-4 py-3 w-full text-sm font-medium bg-white focus:border-[#0b9949] focus:ring-4 focus:ring-green-50 focus:outline-none transition-all duration-300"
                    onChange={handleChange}
                    value={formData.email}
                    required
                  />
                </div>
              </div>

              {/* ID Type & NRIC */}
              <div className="flex flex-col md:col-span-2 relative group">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 transition-colors group-focus-within:text-[#0b9949]">Identification *</label>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative sm:w-1/3">
                    <select name="idType" className="appearance-none border-2 border-slate-200 rounded-xl px-4 py-3 w-full text-sm font-medium bg-white focus:border-[#0b9949] focus:ring-4 focus:ring-green-50 focus:outline-none transition-all duration-300" onChange={handleChange} value={formData.idType} required>
                      <option value="">Select ID Type</option>
                      <option value="NRIC">NRIC</option>
                      <option value="FIN">FIN</option>
                      <option value="Passport">Passport</option>
                    </select>
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">▼</span>
                  </div>
                  <div className="relative sm:w-2/3">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"><IdCard className="w-4 h-4" /></span>
                    <input
                      name="nric"
                      placeholder="Enter ID Number"
                      className="border-2 border-slate-200 rounded-xl pl-10 pr-4 py-3 w-full text-sm font-medium bg-white focus:border-[#0b9949] focus:ring-4 focus:ring-green-50 focus:outline-none transition-all duration-300"
                      onChange={handleChange}
                      value={formData.nric}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* DOB & Contact */}
              <div className="flex flex-col relative group">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 transition-colors group-focus-within:text-[#0b9949]">Date of Birth</label>
                <div className="relative">
                  <input
                    type="date"
                    name="dob"
                    max={new Date().toISOString().split("T")[0]}
                    className="border-2 border-slate-200 rounded-xl px-4 py-3 w-full text-sm font-medium bg-white focus:border-[#0b9949] focus:ring-4 focus:ring-green-50 focus:outline-none transition-all duration-300"
                    onChange={handleChange}
                    value={formData.dob}
                  />
                </div>
              </div>

              <div className="flex flex-col relative group">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 transition-colors group-focus-within:text-[#0b9949]">Contact Number (Optional)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"><Phone className="w-4 h-4" /></span>
                  <input
                    type="tel"
                    inputMode="tel"
                    name="contact"
                    placeholder="Enter contact number"
                    className="border-2 border-slate-200 rounded-xl pl-10 pr-4 py-3 w-full text-sm font-medium bg-white focus:border-[#0b9949] focus:ring-4 focus:ring-green-50 focus:outline-none transition-all duration-300"
                    onChange={handleChange}
                    onKeyPress={(e) => {
                      if (!/[0-9+\-()\s]/.test(e.key)) e.preventDefault();
                    }}
                    value={formData.contact}
                  />
                </div>
              </div>

              {/* Gender, Nationality, Race */}
              <div className="flex flex-col relative group">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 transition-colors">Gender</label>
                <div className="flex items-center space-x-6 h-12">
                  <label className="flex items-center space-x-2 cursor-pointer font-medium text-gray-700">
                    <input type="radio" name="gender" value="Male" onChange={handleChange} checked={formData.gender === "Male"} className="w-4 h-4 text-[#0b9949] focus:ring-[#0b9949] border-gray-300" />
                    <span>Male</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer font-medium text-gray-700">
                    <input type="radio" name="gender" value="Female" onChange={handleChange} checked={formData.gender === "Female"} className="w-4 h-4 text-[#0b9949] focus:ring-[#0b9949] border-gray-300" />
                    <span>Female</span>
                  </label>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex flex-col relative group w-full">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 transition-colors group-focus-within:text-[#0b9949]">Nationality</label>
                  <SearchableDropdown
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    options={lookups?.nationalities || []}
                    placeholder="Select Nationality"
                  />
                </div>

                <div className="flex flex-col relative group w-full">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 transition-colors group-focus-within:text-[#0b9949]">Race</label>
                  <SearchableDropdown
                    name="race"
                    value={formData.race}
                    onChange={handleChange}
                    options={lookups?.races || []}
                    placeholder="Select Race"
                  />
                </div>
              </div>

              {/* NRIC / Work Permit Upload */}
              <div className="flex flex-col md:col-span-2 relative group mt-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 transition-colors group-focus-within:text-[#0b9949]">NRIC / Work Permit Upload (Optional)</label>
                <div className="relative border-2 border-dashed border-slate-300 rounded-xl p-4 bg-slate-50/50 hover:bg-slate-50 hover:border-[#0b9949] transition-all duration-300 flex flex-col items-center justify-center text-center">
                  <input
                    type="file"
                    name="workPermit"
                    onChange={(e) => setFormData({ ...formData, workPermit: e.target.files[0] })}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  {!formData.workPermit ? (
                    <div className="pointer-events-none">
                      <IdCard className="text-gray-500 mb-2 block mx-auto" size={32} />
                      <p className="text-sm font-semibold text-slate-700">NRIC / Work Permit File</p>
                      <p className="text-xs text-slate-500 mt-1">Upload NRIC or Work Permit (PDF, JPG, PNG) - Max 1 MB</p>
                    </div>
                  ) : (
                    <div className="pointer-events-none flex flex-col items-center">
                      <CheckCircle2 className="text-green-500 mb-2" size={32} />
                      <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1.5 rounded-lg text-xs font-bold">
                        <span>{formData.workPermit.name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* WSQ Certificate Upload */}
              <div className="flex flex-col md:col-span-2 relative group mt-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 transition-colors group-focus-within:text-[#0b9949]">WSQ Certificate Upload (Optional)</label>
                <div className="relative border-2 border-dashed border-slate-300 rounded-xl p-4 bg-slate-50/50 hover:bg-slate-50 hover:border-[#0b9949] transition-all duration-300 flex flex-col items-center justify-center text-center">
                  <input
                    type="file"
                    name="wsqCert"
                    onChange={(e) => setFormData({ ...formData, wsqCert: e.target.files[0] })}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.bmp"
                  />
                  {!formData.wsqCert ? (
                    <div className="pointer-events-none">
                      <ClipboardList className="text-gray-500 mb-2 block mx-auto" size={32} />
                      <p className="text-sm font-semibold text-slate-700">WSQ Certificate File</p>
                      <p className="text-xs text-slate-500 mt-1">Upload WSQ Certificate (PDF, JPG, PNG, DOC, Excel, BMP) - Max 1 MB</p>
                    </div>
                  ) : (
                    <div className="pointer-events-none flex flex-col items-center">
                      <CheckCircle2 className="text-green-500 mb-2" size={32} />
                      <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1.5 rounded-lg text-xs font-bold">
                        <span>{formData.wsqCert.name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Education Certificate */}
              <div className="flex flex-col md:col-span-2 relative group mt-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 transition-colors group-focus-within:text-[#0b9949]">Education Certificate (Optional)</label>
                <div className="relative border-2 border-dashed border-slate-300 rounded-xl p-4 bg-slate-50/50 hover:bg-slate-50 hover:border-[#0b9949] transition-all duration-300 flex flex-col items-center justify-center text-center">
                  <input
                    type="file"
                    name="educationCert"
                    onChange={(e) => setFormData({ ...formData, educationCert: e.target.files[0] })}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept=".pdf,.jpg,.jpeg,.png"
                  />
                  {!formData.educationCert ? (
                    <div className="pointer-events-none">
                      <ClipboardList className="text-gray-500 mb-2 block mx-auto" size={32} />
                      <p className="text-sm font-semibold text-slate-700">Highest Education Certificate</p>
                      <p className="text-xs text-slate-500 mt-1">Upload your highest education certificate (PDF, JPG, PNG) - Max 1 MB</p>
                    </div>
                  ) : (
                    <div className="pointer-events-none flex flex-col items-center">
                      <CheckCircle2 className="text-green-500 mb-2" size={32} />
                      <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1.5 rounded-lg text-xs font-bold">
                        <span>{formData.educationCert.name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* OTHER DETAILS SECTION */}
          <section className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
            <h2 className="text-2xl font-bold text-slate-800 mb-6 pb-2 border-b-2 border-slate-100 flex items-center gap-3">
              <span className="bg-[#0b9949] text-white w-8 h-8 rounded-lg flex items-center justify-center text-sm">2</span>
              Other Details
            </h2>
            
            {/* Know How */}
            <div className="md:col-span-2">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                How did you hear about us?
              </label>
            {["Newspaper/Website", "Course leaflet/flyer", "Friend/Colleague", "Classroom Ad", "Others"].map((option) => (
              <label key={option} className="flex items-center space-x-2 mb-1">
                <input
                  type="radio"
                  name="knowHow"
                  value={option}
                  onChange={handleChange}
                  checked={formData.knowHow === option}
                  className="focus:ring-2 focus:ring-[#0b9949]"
                />
                <span>{option}</span>
              </label>
            ))}
          </div>

          {/* E-Cert */}
          {/* <div className="flex items-center space-x-4 md:col-span-2">
            <label className="font-semibold">Do you want e-certificate?</label>
            <label className="flex items-center space-x-1">
              <input
                type="radio"
                name="eCert"
                value="Yes"
                onChange={handleChange}
                checked={formData.eCert === "Yes"}
                className="focus:ring-2 focus:ring-[#0b9949]"
              />
              <span>Yes</span>
            </label>
            <label className="flex items-center space-x-1">
              <input
                type="radio"
                name="eCert"
                value="No"
                onChange={handleChange}
                checked={formData.eCert === "No"}
                className="focus:ring-2 focus:ring-[#0b9949]"
              />
              <span>No</span>
            </label>
          </div> */}

            {/* Staff Referral */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="mb-4">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Do you know our staff?</label>
              <div className="flex gap-6">
                <label className="flex items-center space-x-2 cursor-pointer font-medium text-gray-700">
                  <input
                    type="radio"
                    name="knowStaff"
                    value="Yes"
                    checked={formData.knowStaff === "Yes"}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#0b9949] focus:ring-[#0b9949] border-gray-300"
                  />
                  <span>Yes</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer font-medium text-gray-700">
                  <input
                    type="radio"
                    name="knowStaff"
                    value="No"
                    checked={formData.knowStaff === "No"}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#0b9949] focus:ring-[#0b9949] border-gray-300"
                  />
                  <span>No</span>
                </label>
              </div>
            </div>

            <AnimatePresence>
              {formData.knowStaff === "Yes" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="grid md:grid-cols-2 gap-6 mt-2 pt-2 pb-4">
                    {/* Staff Name Input */}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Sales Staff Name *</label>
                      <div className="relative">
                        <input
                          type="text"
                          name="staffName"
                          placeholder="e.g. jack"
                          value={formData.staffName}
                          onChange={handleChange}
                          required={formData.knowStaff === "Yes"}
                          autoComplete="off"
                          className={`border-2 rounded-xl px-4 py-3 w-full text-sm font-semibold tracking-wide pr-12 transition-all duration-300 outline-none
                            ${staffLookupState === 'found' ? 'border-emerald-400 bg-emerald-50/30 focus:border-emerald-500' : 
                              staffLookupState === 'not_found' ? 'border-amber-300 bg-amber-50/20 focus:border-amber-400' : 
                              staffLookupState === 'error' ? 'border-red-300 bg-red-50/20' :
                              'border-slate-200 bg-white focus:border-[#0b9949]'}`}
                        />
                        {/* Status Icon */}
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-base">
                          {staffLookupState === 'loading' && (
                            <svg className="animate-spin w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                            </svg>
                          )}
                          {staffLookupState === 'found' && <Check className="text-emerald-500 stroke-[3]" size={20} />}
                          {staffLookupState === 'not_found' && <span className="text-amber-400 font-bold text-lg">?</span>}
                          {staffLookupState === 'error' && <span className="text-red-400 font-bold text-lg">!</span>}
                        </div>
                      </div>

                      {/* Live status message */}
                      <AnimatePresence mode="wait">
                        {staffLookupState === 'found' && (
                          <motion.div
                            key="found"
                            initial={{ opacity: 0, y: -6, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                            exit={{ opacity: 0, y: -6, height: 0 }}
                            className="mt-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3"
                          >
                            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                              <Check className="text-white" size={14} strokeWidth={4} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-extrabold text-emerald-800 uppercase tracking-wide">Valid Sales Staff</p>
                              <p className="text-sm font-bold text-emerald-700 mt-0.5">{staffOfficialName}</p>
                              <p className="text-[10px] text-emerald-600 mt-0.5 flex items-center gap-1">Official sales email auto-filled <ArrowDown size={12} /></p>
                            </div>
                          </motion.div>
                        )}
                        {staffLookupState === 'not_found' && (
                          <motion.div
                            key="not_found"
                            initial={{ opacity: 0, y: -6, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                            exit={{ opacity: 0, y: -6, height: 0 }}
                            className="mt-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2.5"
                          >
                            <div className="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                              <span className="text-white text-xs font-extrabold">?</span>
                            </div>
                            <div>
                              <p className="text-xs font-extrabold text-amber-800 uppercase tracking-wide">Not Found</p>
                              <p className="text-sm font-semibold text-amber-700 mt-0.5">Sales staff name not found in system.</p>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Staff Email Input */}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Sales Staff Email</label>
                      <input
                        type="email"
                        name="staffEmail"
                        placeholder="Sales Staff Email (e.g. name@greenmark.com.sg)"
                        value={formData.staffEmail}
                        onChange={handleChange}
                        readOnly={staffLookupState === 'found'}
                        className={`border-2 rounded-xl px-4 py-3 w-full text-sm font-semibold tracking-wide transition-all duration-300 outline-none
                          ${staffLookupState === 'found' 
                            ? 'border-emerald-300 bg-gray-50 text-gray-500 cursor-not-allowed' 
                            : 'border-slate-200 bg-white focus:border-[#0b9949]'}`}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            </div>
          </section>

          {/* 🟢 reCAPTCHA v3 Badge (required by Google) */}
          <div className="grecaptcha-badge" data-style="inline"></div>

          {/* Terms */}
          <div className="md:col-span-2 flex flex-col space-y-2 mt-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="terms"
                onChange={handleChange}
                checked={formData.terms}
                className="focus:ring-2 focus:ring-[#0b9949]"
              />
              <span>I accept Terms and Conditions</span>
            </label>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={!formData.terms || isSubmitting}
            className={`w-full py-4 px-6 rounded-xl mt-8 font-bold text-lg shadow-lg transition-all duration-300 flex items-center justify-center gap-2 ${formData.terms && !isSubmitting
              ? "bg-[#0b9949] hover:bg-[#097c3c] text-white cursor-pointer hover:shadow-xl hover:-translate-y-1"
              : "bg-slate-200 text-slate-500 cursor-not-allowed opacity-80"
              }`}
          >
            {!formData.terms ? <Lock size={20} /> : (isSubmitting ? <Loader2 size={20} className="animate-spin" /> : <CheckCircle2 size={20} />)}
            {isSubmitting ? "Processing Registration..." : (formData.terms ? "Confirm Registration" : "Accept Terms to Submit")}
          </motion.button>
        </form>
        </div>
      </div>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccessModal && (
          <SuccessModal onClose={handleModalClose} />
        )}
      </AnimatePresence>


    </>
  );
}